import React, { useState, FC, ReactNode } from 'react';

import styled from 'styled-components';

import { Box } from '@adsk/alloy-react-box';
import { BasicButton } from '@adsk/alloy-react-button';
import {
  TriangleUpIcon,
  TriangleDownIcon,
  GearIcon,
} from '@adsk/alloy-react-icon';
import { WithAlloySuspense } from '@adsk/alloy-react-suspense';
import theme from '@adsk/alloy-react-theme';
import Tooltip, { Flyout } from '@adsk/alloy-react-tooltip';
import { useSideNavigation } from '@adsk/alloy-side-navigation';

import ProductLoader from './ProductLoader';
import ProductPicker from './ProductPicker';
import { Product } from '../helpers/types';
import { useTranslation } from '../i18n';

const StyledLabel = styled(Box)`
  ${{
    [theme.mediaQuery.small]: {
      display: 'none',
    },
  }}
`;

// TODO - can be removed when account entitlement is available in V2
// TODO - verify it works as expected
const getProductIconV1 = (product: Product) =>
  product?.renderIcon?.({
    size: 24,
    style: { color: theme.colors.adskBlue500 },
  });

type ProductButtonProps = {
  productId?: string;
  required?: boolean;
  products?: Product[];
  renderCustomHeader?: () => ReactNode;
  onProductClick?: ({ id, url }: { [key: string]: string }) => void;
};

/**
 * Acs TopNavigation
 */
const ProductButton: FC<ProductButtonProps> = ({
  products,
  productId,
  required = true,
  renderCustomHeader,
  onProductClick,
}) => {
  const { t } = useTranslation();
  const { collapsed } = useSideNavigation();
  const [menuOpen, setMenuOpen] = useState(false);
  const PLACEHOLDER = {
    name: t('PRODUCTS') as string,
    renderIcon: GearIcon.renderIcon,
    iconUrl: '',
    key: '',
    defaultUrl: '',
  };

  const currentProduct =
    products?.find((product) => product.key === productId) ||
    (!required && PLACEHOLDER);

  const TriangleIconComponent = menuOpen ? TriangleUpIcon : TriangleDownIcon;

  return currentProduct ? (
    <Flyout
      show={menuOpen}
      onHide={() => setMenuOpen(false)}
      onShow={() => setMenuOpen(true)}
      offset={15}
      renderBody={(props) => <Tooltip.Body {...props} padding={0} />}
      placement={Flyout.PLACEMENTS.BOTTOM_START}
      renderTarget={(props) => <Tooltip.Target {...props} />}
      content={
        <ProductPicker
          selectedId={productId}
          products={products}
          renderCustomHeader={renderCustomHeader}
          onClose={() => setMenuOpen(false)}
          onProductClick={onProductClick}
        />
      }
    >
      <BasicButton
        onClick={() => setMenuOpen(!menuOpen)}
        data-testid="TopNavigation__products"
        style={{
          padding: 0,
          width: collapsed ? undefined : 195,
          justifyContent: 'start',
          color: theme.colors.charcoal900,
          '&:hover': {
            color: theme.colors.charcoal700,
          },
          [theme.mediaQuery.small]: {
            width: 'auto',
          },
        }}
      >
        <Box
          display="inline-flex"
          borderRadius="4px"
          width="32px"
          height="32px"
          minWidth="24px"
          bg="adskBlue100"
          alignItems="center"
          justifyContent="center"
        >
          {currentProduct.iconUrl ? (
            <Box
              bg="adskBlue500"
              width="24px"
              height="24px"
              style={{
                maskImage: `url(${currentProduct.iconUrl})`,
              }}
            />
          ) : (
            getProductIconV1(currentProduct)
          )}
        </Box>
        {!collapsed && (
          <StyledLabel
            {...(currentProduct.name?.length > 15
              ? { typography: 'bodySmallBold', textAlign: 'left' }
              : { typography: 'bodyMediumBold' })}
            paddingLeft="8px"
            maxWidth="250px"
          >
            {currentProduct.name}
          </StyledLabel>
        )}
        <TriangleIconComponent
          style={{
            color: theme.colors.charcoal700,
            margin: '0 4px',
            [theme.mediaQuery.small]: {
              visibility: 'hidden',
            },
          }}
          size={20}
        />
      </BasicButton>
    </Flyout>
  ) : (
    <ProductLoader />
  );
};

ProductButton.displayName = 'ProductButton';

export default WithAlloySuspense(ProductButton);
